import { useViewModel } from '@descomplica/microfrontend-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { FullPageError } from '@/shared/presentation/components/FullPageError';
import { FullPageLoading } from '@/shared/presentation/components/FullPageLoading';
import { Button } from '@/shared/presentation/components/ui/Button';
import { Input } from '@/shared/presentation/components/ui/Input';
import { PhoneInput } from '@/shared/presentation/components/ui/PhoneInput';
import { Space } from '@/shared/presentation/components/ui/Space';

import LogoSvg from '../../assets/logo.svg';
import { Checkbox } from '../../components/Checkbox';
import { ErrorMessage } from '../../components/ErrorMessage';
import { SignInWithSSOViewModel } from './SignInWithSSO.vm';

const Container = styled.div`
  max-width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--gray-02);

  @media screen and (max-width: 480px) {
    background-color: white;

    button:first-child {
      width: 100%;
    }
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background-color: var(--white);
  border-radius: 16px;
  color: #000;
  padding: 48px;

  @media screen and (max-width: 480px) {
    padding: 24px;
    height: 100vh;
    border-radius: 0;
    max-width: calc(100vw - 48px);
  }
`;

const Logo = styled.img.attrs({
  src: `${LogoSvg}`,
})`
  width: auto;
  max-width: 128px;
  margin: 0 auto 24px;
  display: block;
`;

const Form = styled.form`
  display: block;
`;

const Title = styled.h1`
  font-size: 32px;
  line-height: 1.2;
  color: var(--gray-90);
  margin-bottom: 16px;
`;

const Subtitle = styled.div`
  font-size: 16px;
  line-height: 20px;
  color: #636769;
  font-weight: normal;
`;

const Terms = styled.div`
  a {
    color: var(--green-dark);
  }
`;

const termsOfUseUrl = 'https://accounts.descomplica.com.br/termos-uso/';
const privacyPolicyUrl = 'https://accounts.descomplica.com.br/politica-privacidade/';

export const SignInWithSSOScreen = observer(() => {
  const navigate = useNavigate();
  const params = useParams();

  const vm = useViewModel(SignInWithSSOViewModel, {
    navigate,
    provider: params.provider as string,
  });

  const signUp = (e) => {
    e.preventDefault();
    vm.signUp();
  };

  if (vm.isLoading) {
    return <FullPageLoading />;
  }

  if (vm.hasSignInError) {
    return <FullPageError errorMessage="Ocorreu um erro ao tentar fazer login com o SSO" />;
  }

  return (
    <Container>
      <Content>
        <Form onSubmit={signUp}>
          <Logo />
          <Title>Confirmar cadastro</Title>
          <Subtitle>Finalize seu cadastro para acessar o Descomplica</Subtitle>

          <Space height={8} />

          {!!vm.hasSignUpError && (
            <ErrorMessage message="Ocorreu um erro ao tentar finalizar o cadastro" />
          )}

          <Space height={16} />

          <Input
            placeholder="E-mail"
            label="E-mail"
            name="email"
            errorMessage={vm.form.errors.email && vm.form.errors.email[0]}
            onChange={(value) => vm.form.setValue('email', value)}
            onBlur={() => vm.form.validateField('email')}
            value={vm.form.values.email}
            autoFocus
            required
          />

          <Space height={16} />

          <Input
            placeholder="Nome completo"
            label="Nome completo"
            name="name"
            errorMessage={vm.form.errors.name && vm.form.errors.name[0]}
            onChange={(value) => vm.form.setValue('name', value)}
            onBlur={() => vm.form.validateField('name')}
            value={vm.form.values.name}
            required
          />

          <Space height={16} />

          <PhoneInput
            placeholder="Telefone"
            label="Telefone"
            name="phoneNumber"
            errorMessage={vm.form.errors.phoneNumber && vm.form.errors.phoneNumber[0]}
            onChange={(value) => vm.form.setValue('phoneNumber', value)}
            onBlur={() => vm.form.validateField('phoneNumber')}
            value={vm.form.values.phoneNumber}
          />

          <Space height={16} />

          <Input
            placeholder="Senha"
            label="Senha"
            type="password"
            name="password"
            feedbackMessage={
              'A senha deve conter pelo menos 6 caracteres, uma letra maiúscula, uma letra minúscula e um número.'
            }
            status="info"
            errorMessage={vm.form.errors.password && vm.form.errors.password[0]}
            onChange={(value) => vm.form.setValue('password', value)}
            onBlur={() => vm.form.validateField('password')}
            required
          />

          <Space height={16} />

          <Input
            placeholder="Confirmar senha"
            label="Confirmar senha"
            type="password"
            name="passwordConfirmation"
            errorMessage={
              vm.form.errors.passwordConfirmation && vm.form.errors.passwordConfirmation[0]
            }
            onChange={(value) => vm.form.setValue('passwordConfirmation', value)}
            onBlur={() => vm.form.validateField('passwordConfirmation')}
            required
          />

          <Space height={16} />

          <Terms>
            <Checkbox
              name="isTermsAccepted"
              checked={vm.form.values.isTermsAccepted}
              onChange={(value) => {
                vm.form.setValueAndValidateValue('isTermsAccepted', value);
              }}
              label={`<span>Aceitar os</span> <a href=${termsOfUseUrl} target="_blank" rel="noreferrer">Termos de Uso</a>  e  <a href="${privacyPolicyUrl}" target="_blank" rel="noreferrer">Política de Privacidade</a>`}
              errorMessage={vm.form.errors.isTermsAccepted && vm.form.errors.isTermsAccepted[0]}
              aria-label="Checkbox para aceitar os termos de uso"
            />
          </Terms>

          <Space height={32} />

          <Button
            fullWidth
            loading={vm.isSubmitting}
            disabled={!vm.form.isValid || vm.isSubmitting}
            variant="primary"
            type="submit"
            aria-label="Botão para cadastrar"
          >
            Cadastrar
          </Button>
        </Form>
      </Content>
    </Container>
  );
});
